import { useEffect, useRef, useState } from "react";
import { X, ExternalLink, Star, Youtube, MessageSquare, Loader2, ShieldCheck, Lock, MapPin, Truck, Store, TriangleAlert, Navigation, Heart } from "lucide-react";
import { getTrustBadge } from "./dealFeedData";
import { getPdpReviews, getPdpLinkStatus } from "@/lib/api";
import { snagOutbound, snagMapLocation } from "@/lib/outbound";
import { canViewPdpReviews, TIER_CONFIG } from "@/lib/tierGate";
import { SafeImg } from "@/components/SafeImg";

const FLAT_TAX_ESTIMATE = 0.0725;

// Mobile Video Review Exit Fix — resolve any YouTube watch/short/embed URL to a clean
// embeddable video ID so review videos play in an in-app lightbox (not a bare new tab)
// with a guaranteed, always-visible close control.
function youtubeVideoId(link) {
  try {
    const u = new URL(link);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1) || null;
    if (u.pathname.includes("/embed/")) return u.pathname.split("/embed/")[1] || null;
    return u.searchParams.get("v") || null;
  } catch {
    return null;
  }
}

// PDP (Product Detail) — modal overlay (no route). Reached ONLY via "Review Product"
// across Scroll/Discover/Daily Deck; "Snag Deal" here is the ONLY button anywhere in
// the app that actually fires the monetized affiliate redirect (`snagOutbound`).
export default function PdpModal({ deal, open, onClose, currentTier = "FREE", location, onLocked, onOpenPdp, isSaved = false, onToggleSave }) {
  const [reviews, setReviews] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linkStatus, setLinkStatus] = useState(null);
  const [videoModal, setVideoModal] = useState(null); // {id, title} — in-app YouTube lightbox
  const warnedRef = useRef(false);
  const reviewsGate = canViewPdpReviews(currentTier);
  const cfg = TIER_CONFIG[currentTier] || TIER_CONFIG.FREE;

  useEffect(() => {
    if (!open || !deal || reviewsGate.locked) { setReviews(null); return; }
    warnedRef.current = false;
    setLoading(true);
    getPdpReviews(deal.title)
      .then((r) => {
        setReviews(r);
        if (r?.source === "seed" && !warnedRef.current) {
          warnedRef.current = true;
          // eslint-disable-next-line no-console
          console.warn("[Snaggedify Alpha] Live review search is unavailable right now — showing sample reviews for this product.");
        }
      })
      .catch(() => setReviews(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deal, reviewsGate.locked]);

  // Link Availability Engine — on-demand check fired the moment a PDP opens (Section:
  // Out of Stock/Expired detection). Map results skip this — they're Maps links, not
  // product pages that can go out of stock.
  useEffect(() => {
    if (!open || !deal || deal.isMapResult || !deal.outboundUrl) { setLinkStatus(null); return; }
    setLinkStatus(null);
    getPdpLinkStatus({ url: deal.outboundUrl, title: deal.title, domain: deal.reseller, category: "PRODUCT" })
      .then(setLinkStatus)
      .catch(() => setLinkStatus(null));
  }, [open, deal]);

  useEffect(() => {
    if (!open || !deal) { setVideoModal(null); return; }
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (videoModal) { setVideoModal(null); return; }
      onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, deal, onClose, videoModal]);

  if (!open || !deal) return null;

  const badge = getTrustBadge(deal.verdict);
  const fulfillmentMode = deal.isMapResult ? "IN_STORE_PICKUP" : (deal.fulfillment || "ONLINE_SHIPPING");
  const isUnavailable = !deal.isMapResult && linkStatus?.status === "archived";
  const displayBadge = isUnavailable ? { Icon: TriangleAlert, label: "UNAVAILABLE", className: "border border-slate-300 bg-slate-400 text-white dark:border-white/10 dark:bg-zinc-600" } : badge;
  const manufacturer = deal.manufacturer || "Manufacturer";
  const hasPrice = !deal.isMapResult || !!deal.price;
  const msrp = deal.msrp ?? deal.price;
  const savings = Math.max(0, msrp - deal.price);
  const savingsPct = msrp > 0 ? Math.round((savings / msrp) * 100) : 0;

  // Location-based tax: exact ZIP/State rate for Plus+ (reuses the existing Settings
  // location system), flat estimate for Free.
  const hasExactTax = cfg.exactLocationTax && location?.tax_rate != null;
  const taxRate = hasExactTax ? location.tax_rate : FLAT_TAX_ESTIMATE;
  const taxAmount = deal.price * taxRate;
  const totalWithTax = deal.price + taxAmount;
  const taxLabel = hasExactTax ? `${location.locality || location.state_name} exact tax` : "Est. Tax (set your ZIP in Settings for exact tax)";

  return (
    <div id="pdp-modal" data-testid="pdp-modal" className="fixed inset-0 z-[170] flex items-end justify-center bg-black/70 backdrop-blur-md sm:items-center sm:p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl dark:border-white/10 dark:bg-[#121214] sm:rounded-3xl"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-white/10">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`trust-badge inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-black uppercase tracking-wide ${displayBadge.className}`} data-testid="pdp-trust-badge">
              <displayBadge.Icon className="h-3.5 w-3.5" strokeWidth={2.75} /> {displayBadge.label}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wide text-slate-500 dark:border-white/10 dark:bg-white/10 dark:text-zinc-400" data-testid="pdp-fulfillment-badge">
              {fulfillmentMode === "IN_STORE_PICKUP" ? <><Store className="h-3.5 w-3.5" /> In-Store Pickup</> : <><Truck className="h-3.5 w-3.5" /> Online Shipping</>}
            </span>
          </div>
          <button onClick={onClose} data-testid="pdp-close-btn" aria-label="Close" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10 dark:hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="flex gap-3">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100 dark:bg-white/10">
              {deal.image ? <SafeImg src={deal.image} alt={deal.title} className="h-full w-full object-cover" /> : <ShieldCheck className="h-8 w-8 text-slate-300" />}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="line-clamp-2 text-sm font-black text-slate-900 dark:text-white" data-testid="pdp-title">{deal.title}</h3>
              {!deal.isMapResult && <p className="mt-1 truncate text-xs font-bold text-slate-500 dark:text-zinc-400" data-testid="pdp-manufacturer">Manufacturer: {manufacturer}</p>}
              <p className="mt-0.5 truncate text-xs font-bold text-slate-500 dark:text-zinc-400" data-testid="pdp-reseller">Reseller: {deal.reseller || "Unknown"}</p>
            </div>
            {onToggleSave && (
              <button
                onClick={onToggleSave}
                data-testid="pdp-save-btn"
                aria-label={isSaved ? "Remove from Vault" : "Save to Vault"}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${isSaved ? "border-rose-300 bg-rose-50 text-rose-500 dark:border-rose-500/30 dark:bg-rose-500/10" : "border-slate-200 text-slate-400 hover:text-rose-500 dark:border-white/10 dark:text-zinc-500"}`}
              >
                <Heart className="h-4 w-4" fill={isSaved ? "currentColor" : "none"} />
              </button>
            )}
          </div>

          {/* OEM vs. Reseller price comparison — baseline label shown to ALL tiers.
              Skipped entirely for Map results without a real price (gas/local listings
              often carry no scraped price). */}
          {hasPrice && (
            <>
              <div className="mt-4 grid grid-cols-2 gap-3" data-testid="pdp-price-comparison">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-center dark:border-white/10 dark:bg-white/5" data-testid="pdp-original-price">
                  <p className="text-[9px] font-black uppercase tracking-wide text-slate-400">{manufacturer} MSRP</p>
                  <p className="mt-1 text-lg font-black text-slate-500 line-through dark:text-zinc-400">${msrp.toFixed(2)}</p>
                </div>
                <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-3 text-center" data-testid="pdp-verified-price">
                  <p className="text-[9px] font-black uppercase tracking-wide text-emerald-600">{deal.reseller || "Reseller"} Price</p>
                  <p className="mt-1 text-lg font-black text-emerald-600">${deal.price.toFixed(2)}</p>
                </div>
              </div>
              {savings > 0 && <p className="mt-2 text-center text-xs font-bold text-emerald-600" data-testid="pdp-savings">You save ${savings.toFixed(2)}{cfg.pdpDetailedBreakdown && ` (${savingsPct}% off MSRP)`}</p>}

              {/* Location-based tax */}
              <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-xs dark:bg-white/5" data-testid="pdp-tax-row">
                <span className="flex items-center gap-1.5 font-bold text-slate-500 dark:text-zinc-400"><MapPin className="h-3.5 w-3.5" /> {taxLabel}</span>
                <span className="font-black text-slate-900 dark:text-white" data-testid="pdp-tax-amount">+${taxAmount.toFixed(2)}</span>
              </div>
              <p className="mt-1 text-right text-xs font-black text-slate-900 dark:text-white" data-testid="pdp-total-with-tax">Est. Total: ${totalWithTax.toFixed(2)}</p>
            </>
          )}

          {/* Detailed OEM/Reseller breakdown — Pro+ only */}
          {cfg.pdpDetailedBreakdown && deal.sources?.length > 1 && (
            <div className="mt-4" data-testid="pdp-detailed-breakdown">
              <p className="mb-2 text-[10px] font-black uppercase tracking-wide text-slate-400">Reseller Price Breakdown</p>
              <ul className="space-y-1">
                {deal.sources.map((s, i) => (
                  <li key={i} data-testid={`pdp-breakdown-row-${i}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-xs dark:bg-white/5">
                    <span className="font-bold text-slate-700 dark:text-zinc-300">{s.reseller}</span>
                    <span className="font-black text-slate-900 dark:text-white">${s.price.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Multi-source reviews — Pro+ only, per the tier matrix */}
          <div className="mt-5 space-y-4" data-testid="pdp-reviews-section">
            {reviewsGate.locked ? (
              <button
                onClick={() => { onLocked?.(reviewsGate.requiredTier); onClose?.(); }}
                data-testid="pdp-reviews-locked"
                className="flex w-full flex-col items-center justify-center gap-1.5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center dark:border-white/10 dark:bg-white/5"
              >
                <Lock className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
                <p className="text-[11px] font-black uppercase leading-tight text-slate-500 dark:text-zinc-400">Unlock YouTube/TikTok/Reddit Reviews with {reviewsGate.requiredTier}</p>
              </button>
            ) : loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm font-bold text-slate-400" data-testid="pdp-reviews-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading reviews…
              </div>
            ) : reviews ? (
              <>
                {reviews.google && (
                  <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 dark:bg-white/5" data-testid="pdp-google-rating">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    <span className="text-sm font-black text-slate-900 dark:text-white">{reviews.google.rating ?? "—"}</span>
                    <span className="text-xs text-slate-400">({reviews.google.rating_count ?? 0} ratings) · Google</span>
                  </div>
                )}

                {reviews.youtube?.length > 0 && (
                  <div data-testid="pdp-youtube-reviews">
                    <p className="mb-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide text-slate-400"><Youtube className="h-3.5 w-3.5" /> YouTube Reviews</p>
                    <div className="grid grid-cols-2 gap-2">
                      {reviews.youtube.map((v, i) => {
                        const vid = v.link !== "#" ? youtubeVideoId(v.link) : null;
                        const Tag = vid ? "button" : "a";
                        return (
                          <Tag
                            key={i}
                            {...(vid ? { onClick: () => setVideoModal({ id: vid, title: v.title }) } : { href: v.link !== "#" ? v.link : undefined, target: "_blank", rel: "noopener noreferrer" })}
                            data-testid={`pdp-youtube-item-${i}`}
                            className="group relative block overflow-hidden rounded-lg border border-slate-200 text-left dark:border-white/10"
                          >
                            {v.thumbnail && <SafeImg src={v.thumbnail} alt={v.title} className="h-16 w-full object-cover" iconClassName="h-5 w-5 text-slate-300 dark:text-zinc-600" />}
                            {vid && (
                              <span className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity group-hover:opacity-100">
                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90"><Youtube className="h-4 w-4 text-red-600" /></span>
                              </span>
                            )}
                            <p className="line-clamp-2 p-1.5 text-[10px] font-bold text-slate-700 dark:text-zinc-300">{v.title}</p>
                          </Tag>
                        );
                      })}
                    </div>
                  </div>
                )}

                {reviews.reddit?.length > 0 && (
                  <div data-testid="pdp-reddit-reviews">
                    <p className="mb-2 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wide text-slate-400"><MessageSquare className="h-3.5 w-3.5" /> Reddit</p>
                    <ul className="space-y-1.5">
                      {reviews.reddit.map((r, i) => (
                        <li key={i} data-testid={`pdp-reddit-item-${i}`} className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-white/5">
                          <p className="text-xs font-bold text-slate-800 dark:text-zinc-200">{r.title}</p>
                          {r.comment && <p className="mt-0.5 text-[11px] text-slate-500 dark:text-zinc-400">"{r.comment}"</p>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {reviews.tiktok?.length > 0 && (
                  <div data-testid="pdp-tiktok-reviews">
                    <p className="mb-2 text-[10px] font-black uppercase tracking-wide text-slate-400">TikTok</p>
                    <ul className="space-y-1.5">
                      {reviews.tiktok.map((t, i) => (
                        <li key={i} data-testid={`pdp-tiktok-item-${i}`} className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-white/5">
                          <p className="text-xs font-bold text-slate-800 dark:text-zinc-200">{t.text}</p>
                          <p className="mt-0.5 text-[10px] text-slate-400">{t.author}{t.likes != null && ` · ${t.likes.toLocaleString()} likes`}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <p className="py-6 text-center text-xs font-bold text-slate-400" data-testid="pdp-reviews-empty">No reviews available for this item yet.</p>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-slate-200 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] dark:border-white/10">
          {deal.isMapResult ? (
            <button
              onClick={() => snagMapLocation({ title: deal.title, address: deal.location?.address, lat: deal.location?.lat, lng: deal.location?.lng, itemId: deal.id })}
              data-testid="pdp-open-maps-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3.5 text-sm font-black uppercase text-white dark:bg-white dark:text-slate-950"
            >
              <Navigation className="h-4 w-4" /> Open in Maps
            </button>
          ) : isUnavailable ? (
            <div data-testid="pdp-unavailable-warning" className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-center dark:border-rose-500/30 dark:bg-rose-500/10">
              <p className="flex items-center justify-center gap-1.5 text-xs font-black uppercase text-rose-600 dark:text-rose-400">
                <TriangleAlert className="h-4 w-4" /> {linkStatus?.availability === "out_of_stock" ? "Out of Stock" : "Link Expired"}
              </p>
              <p className="mt-1 text-[11px] font-bold text-rose-500 dark:text-rose-400/80">This link is no longer available — try a verified alternative below.</p>
            </div>
          ) : (
            <button
              onClick={() => snagOutbound({ url: deal.outboundUrl, itemId: deal.id, merchant: deal.reseller, title: deal.title, price: deal.price })}
              data-testid="pdp-snag-deal-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3.5 text-sm font-black uppercase text-white dark:bg-white dark:text-slate-950"
            >
              <ExternalLink className="h-4 w-4" /> Snag Deal
            </button>
          )}

          {isUnavailable && linkStatus?.alternatives?.length > 0 && (
            <div className="mt-3" data-testid="pdp-alternatives-list">
              <p className="mb-1.5 text-[10px] font-black uppercase tracking-wide text-slate-400">Verified Alternatives</p>
              <ul className="space-y-1.5">
                {linkStatus.alternatives.map((a, i) => (
                  <li key={i} data-testid={`pdp-alternative-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-slate-100 px-2.5 py-1.5 dark:bg-white/10">
                    <span className="truncate text-xs font-bold text-slate-700 dark:text-zinc-300">{a.reseller}</span>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-xs font-black text-brand-orange dark:text-amber-400">${a.price.toFixed(2)}</span>
                      <button
                        onClick={() => onOpenPdp?.({ ...deal, id: `${deal.id}_alt_${i}`, price: a.price, reseller: a.reseller, outboundUrl: a.outbound_url })}
                        data-testid={`pdp-alternative-open-${i}`}
                        className="rounded-md bg-slate-900 px-2 py-1 text-[10px] font-black uppercase text-white dark:bg-white dark:text-slate-950"
                      >
                        View
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!deal.isMapResult && (
            <p className="mt-2 text-center text-[10px] leading-relaxed text-slate-400 dark:text-zinc-500" data-testid="pdp-affiliate-disclosure">
              As an Amazon Associate, we earn from qualifying purchases. Snaggedify may earn a commission on other outbound links too, at zero extra cost to you.
            </p>
          )}
        </div>
      </div>

      {/* Mobile Video Review Exit Fix — in-app lightbox, NOT a full-screen OS webview
          takeover. The close button is a fixed, always-rendered, high-z-index sibling
          of the iframe (never obscured by it), and tapping the backdrop also dismisses. */}
      {videoModal && (
        <div
          data-testid="pdp-video-lightbox"
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setVideoModal(null)}
        >
          <button
            onClick={() => setVideoModal(null)}
            data-testid="pdp-video-lightbox-close-btn"
            aria-label="Close video"
            className="absolute right-4 top-[calc(1rem+env(safe-area-inset-top))] z-[310] flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-md hover:bg-white/25"
          >
            <X className="h-6 w-6" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="relative aspect-video w-full max-w-lg overflow-hidden rounded-xl bg-black">
            <iframe
              key={videoModal.id}
              src={`https://www.youtube.com/embed/${videoModal.id}?autoplay=1&rel=0`}
              title={videoModal.title || "YouTube review"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 h-full w-full border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
}
